import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DialogService } from 'components';

import {
	UniversalSearchComponent,
	UniversalSearchData,
	UniversalSearchResult
} from './universal-search.component';

/** On the rail box while the search is open: drawn as the empty socket it left. */
export const LIFTED_CLASS = 'rail-search--lifted';

export function isMacPlatform(): boolean {
	return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

/** Cmd+K on macOS, Ctrl+K elsewhere — Ctrl+K on a Mac is the text-field kill-line. */
export function isUniversalSearchShortcut(event: KeyboardEvent): boolean {
	if (event.key.toLowerCase() !== 'k' || event.altKey || event.shiftKey) {
		return false;
	}
	const mac = isMacPlatform();
	return mac
		? event.metaKey && !event.ctrlKey
		: event.ctrlKey && !event.metaKey;
}

@Injectable({ providedIn: 'root' })
export class UniversalSearchService {
	private dialogRef: MatDialogRef<
		UniversalSearchComponent,
		UniversalSearchResult
	> | null = null;

	constructor(private dialogService: DialogService) {}

	/**
	 * `anchor` is the rail's search box: the panel grows out of it when opened
	 * and flies back into it when dismissed, and while it is open the
	 * box shows as an empty socket, because the box itself is what moved.
	 */
	open(anchor: HTMLElement | null): void {
		if (this.dialogRef) return;
		const focused = document.activeElement as HTMLElement | null;
		const opener = focused && focused !== document.body ? focused : anchor;
		// The box flies whichever way the search was opened, unless it is not on
		// screen to fly from — the collapsed rail draws it at zero width.
		const morph =
			anchor && anchor.getBoundingClientRect().width > 0 ? anchor : null;
		this.dialogRef = this.dialogService.open<
			UniversalSearchComponent,
			UniversalSearchData,
			UniversalSearchResult
		>(
			UniversalSearchComponent,
			{ anchor, morph },
			{
				panelClass: [
					'custom-no-style-dialog',
					'universal-search-panel'
				],
				backdropClass: 'universal-search-backdrop',
				position: { top: '12vh' },
				ariaLabel: 'Search',
				// TRAP: Escape and the backdrop are handled by the component, so a
				// dismissal can fly back into the rail before the dialog goes.
				disableClose: true,
				exitAnimationDuration: '0ms',
				// TRAP: focus is returned by hand below, because following "Ask Ana"
				// must leave it in Ana's composer, not hand it back to the rail.
				restoreFocus: false
			}
		);
		anchor?.classList.add(LIFTED_CLASS);
		this.dialogRef.afterClosed().subscribe(result => {
			this.dialogRef = null;
			anchor?.classList.remove(LIFTED_CLASS);
			if (result !== 'followed') opener?.focus();
		});
	}
}
